import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button as MuiButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CustomTextInput } from "@ippon/shared-components";

export interface OtherFeeRow {
  label: string;
  amount: string;
}

interface OtherFeesInputProps {
  fees: OtherFeeRow[];
  onChange: (fees: OtherFeeRow[]) => void;
  labelWidth?: string;
  labelWidthSp?: string;
  inputWidth?: string;
  inputWidthSp?: string;
}

const OtherFeesInput: React.FC<OtherFeesInputProps> = ({
  fees,
  onChange,
  labelWidth,
  labelWidthSp = "47%",
  inputWidth,
  inputWidthSp = "100%",
}) => {
  const [newLabel, setNewLabel] = useState("");

  const handleAdd = () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    onChange([...fees, { label: trimmed, amount: "" }]);
    setNewLabel("");
  };

  const handleRemove = (index: number) => {
    onChange(fees.filter((_, i) => i !== index));
  };

  const handleAmountChange = (index: number, value: string) => {
    const next = [...fees];
    next[index] = { ...next[index], amount: value };
    onChange(next);
  };

  const dummyRegister = () => ({
    onChange: () => {},
    onBlur: () => {},
    ref: () => {},
    name: "",
  });

  const makeWatch = (index: number) => (fieldName: string) => {
    if (fieldName === `other_fee_${index}`) {
      return fees[index]?.amount ?? "";
    }
    return "";
  };

  const makeSetValue = (index: number) => (fieldName: string, value: any) => {
    if (fieldName === `other_fee_${index}`) {
      handleAmountChange(index, String(value));
    }
  };

  return (
    <Box>
      {/* タイトル */}
      <Typography
        fontSize={12}
        color="#3E3E3E"
        fontWeight={700}
        sx={{ mb: 0.5 }}
      >
        その他費用
      </Typography>

      {/* 追加済みの費用項目 */}
      {fees.map((fee, index) => (
        <Box key={`other-fee-${index}`}>
          <CustomTextInput
            label={
              (
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  {fee.label}
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(index)}
                    sx={{
                      color: "#3e3e3e",
                      width: 24,
                      height: 24,
                      flexShrink: 0,
                      ml: 1,
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ) as any
            }
            name={`other_fee_${index}`}
            register={dummyRegister}
            errors={{}}
            type="number"
            showYen
            watch={makeWatch(index)}
            setValue={makeSetValue(index)}
            labelWidth={labelWidth}
            labelWidthSp={labelWidthSp}
            inputWidth={inputWidth}
            inputWidthSp={inputWidthSp}
          />
        </Box>
      ))}

      {/* 費用名入力 + 追加ボタン */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          width: { xs: "100%", md: inputWidth || "560px" },
          mt: fees.length > 0 ? 0.5 : 0,
        }}
      >
        <TextField
          placeholder="費用名を入力してください"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              height: "36px",
              "& fieldset": {
                borderColor: "#D9D9D9",
              },
              "&:hover fieldset": {
                borderColor: "#D9D9D9",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#D9D9D9 !important",
                borderWidth: "2px",
              },
            },
            "& .MuiOutlinedInput-input": {
              padding: "8px 12px",
              fontSize: "12px",
              "&::placeholder": {
                fontSize: "12px",
              },
            },
          }}
        />
        <MuiButton
          onClick={handleAdd}
          disabled={!newLabel.trim()}
          sx={{
            height: "36px",
            minWidth: "80px",
            fontSize: "14px",
            fontWeight: 700,
            color: "#fff",
            backgroundColor: "#0B9DBD",
            borderRadius: "8px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#0a8aa5",
            },
            "&:disabled": {
              backgroundColor: "#D9D9D9",
              color: "#999",
            },
          }}
        >
          ＋追加
        </MuiButton>
      </Box>
    </Box>
  );
};

export default OtherFeesInput;
